import React, { useMemo, useCallback, useEffect } from 'react';
import { BreadCrumb } from 'primereact/breadcrumb';

import { useFileManagerContext } from 'modules/FileManagerV2/context/FileManagerContext';

import ListView from './list/ListView';
import GridView from './grid/GridView';

import PreviewModal from '../modals/preview/PreviewModal';
import AddTagModal from '../modals/file/AddTagModal';
import ShareModal from '../modals/file/ShareModal';
import RenameModal from '../modals/file/RenameModal';
import MoveItemModal from '../modals/file/MoveItemModal';
import DeleteConfirmationModal from '../modals/file/DeleteConfirmationModal';
import BulkDeleteConfirmationModal from '../modals/file/BulkDeleteConfirmationModal';
import BulkMoveItemModal from '../modals/file/BulkMoveItemModal';
import FileUploadModal from '../modals/upload/FileUploadModal';
import FileVersionsModal from '../modals/file/FileVersionModal';
import CreateFolderModal from '../modals/folder/CreateFolderModal';
import CreateDocumentModal from '../modals/file/CreateDocumentModal';
import SignatureModal from '../modals/signature/SignatureModal';
import SignatureStatusModal from '../modals/signature/signatureStatus';
import FileSummarizeModal from '../modals/file/SummarizeModal';
import ConfigureSummaryModal from '../modals/file/ConfigureSummaryModal';
import CreateCourtEventModal from '../modals/courtNotice/CreateCourtEventModal';
import SendFaxModal from '../modals/fax/sendFax';
import { useLocation } from 'react-router-dom';
import { queryKeys } from 'hooks/config/queryClient';
import { useQueryClient } from '@tanstack/react-query';
import { useListView } from './list/hooks/useListView';
import { isSharedFolderInBreadcrumbPath } from 'utils/fileManagerSharedFolderUtils';

function FileManagerView() {
  const { search } = useLocation();
  const queryClient = useQueryClient();
  const { setSelectedRows } = useListView();

  const {
    files,
    query,
    modals,
    getFiles,
    pageLimit,
    currentView,
    breadCrumbs,
    caseDetails,
    setBreadCrumbs,
    onFilterChange,
    setPreviewList,
    filterBreadCrumbs,
    fileManagerFilters,
    isGlobalFileManager,
    isClientFileManager,
    selectedPreviewItem,
    setFileManagerFilters,
    setSelectedPreviewItem,
  } = useFileManagerContext();

  const {
    showPreview,
    showShareFileModal,
    showAddTag,
    showRenameModal,
    moveItemModal,
    showDeleteModal,
    showBulkDeleteModal,
    showBulkMoveModal,
    showFileUploadModal,
    showUploadedVersionsModal,
    showCreateFolderModal,
    showCreateDocumentModal,
    showSignatureModal,
    showEsignStatusModal,
    showSummarizeModal,
    showConfigureSummaryModal,
    showCreateEventModal,
    showSendFaxModal,
    setShowPreview,
  } = modals;

  useEffect(() => {
    const fileId = new URLSearchParams(search).get('file_id');

    if (!fileId) {
      setShowPreview(false);
      setSelectedPreviewItem(undefined);
      return;
    }
    if (!files?.length) return;
    // Avoid redundant state updates if the same file is already selected
    if (selectedPreviewItem?.object_id === fileId) return;
    const selectedFile = files?.find((item) => item?.entry_type === 'file' && item?.object_id === fileId);
    if (!selectedFile) return;

    setShowPreview(true);
    setPreviewList(files);
    setSelectedPreviewItem(selectedFile);
  }, [search, files?.length, selectedPreviewItem?.object_id, setShowPreview, setSelectedPreviewItem, setPreviewList]);

  const clearBreadcrumbs = useCallback(() => {
    setBreadCrumbs([]);
  }, [setBreadCrumbs]);

  const BreadCrumbItems = useMemo(() => {
    return breadCrumbs?.map((item) => {
      return {
        label: item.label,
        value: item.value,
        folder_type: item.folder_type ?? null,
        is_virtual: item.is_virtual ?? null,
        is_system_folder: item.is_system_folder ?? null,
        command: (e) => {
          const clickedIndex = breadCrumbs.findIndex((item) => item.value === e.item.value);
          const stillInsideSharedFolder = isSharedFolderInBreadcrumbPath(breadCrumbs, clickedIndex);

          const obj = {
            case_id: isGlobalFileManager || isClientFileManager ? fileManagerFilters.case?.id || '' : caseDetails?.case_id,
            selectedFolder: e.item.value,
            selectedType: fileManagerFilters.fileType,
            tag: fileManagerFilters.tag,
            query: query,
            folder_id: e.item.value,
            is_read: isGlobalFileManager ? fileManagerFilters?.is_read : fileManagerFilters?.fileStatus,
            ...(fileManagerFilters?.shared_folder && { shared_folder: fileManagerFilters.shared_folder }),
          };
          // getFiles(obj, currentView, pageLimit);
          queryClient.invalidateQueries({ queryKey: queryKeys.fileManager.list(obj.case_id) });
          setFileManagerFilters((preVal) => ({
            ...preVal,
            folder_id: e.item.value,
            selectedFolder: e.item.value,
            folder_shared: stillInsideSharedFolder ? true : undefined,
            shared_contact_list: stillInsideSharedFolder ? preVal.shared_contact_list : undefined,
            is_virtual: e.item.is_virtual ?? undefined,
            folder_type: e.item.folder_type ?? undefined,
            is_system_folder: e.item.is_system_folder ?? undefined,
          }));
          filterBreadCrumbs(e.item.value);
        },
      };
    });
  }, [breadCrumbs, getFiles, pageLimit, query, fileManagerFilters, currentView, filterBreadCrumbs]);

  const home = useMemo(
    () => ({
      icon: 'pi pi-home',
      command: () => {
        setSelectedRows([]);
        const data = {
          target: { name: 'selectedFolder' },
          value: '',
          shared_folder: false,
          folder_shared: null,
          shared_contact_list: null,
          is_virtual: null,
          folder_type: null,
          is_system_folder: null,
        };
        onFilterChange(data, clearBreadcrumbs);
      },
    }),
    [onFilterChange, clearBreadcrumbs, setSelectedRows]
  );

  return (
    <>
      <BreadCrumb model={BreadCrumbItems} home={home} />

      {currentView === 'list' ? <ListView /> : <GridView />}
      {showPreview && <PreviewModal />}
      {showAddTag && <AddTagModal />}
      {showShareFileModal && <ShareModal />}
      {showRenameModal && <RenameModal />}
      {moveItemModal && <MoveItemModal />}
      {showDeleteModal && <DeleteConfirmationModal />}
      {showBulkDeleteModal && <BulkDeleteConfirmationModal />}
      {showBulkMoveModal && <BulkMoveItemModal />}
      {showFileUploadModal && <FileUploadModal />}
      {showUploadedVersionsModal && <FileVersionsModal />}
      {showCreateFolderModal && <CreateFolderModal />}
      {showCreateDocumentModal && <CreateDocumentModal />}
      {showSignatureModal && <SignatureModal />}
      {showEsignStatusModal && <SignatureStatusModal />}
      {showSummarizeModal && <FileSummarizeModal />}
      {showConfigureSummaryModal && <ConfigureSummaryModal />}
      {showCreateEventModal && <CreateCourtEventModal />}
      {showSendFaxModal && <SendFaxModal />}
    </>
  );
}

export default FileManagerView;
